import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import {
  debounceTime,
  finalize,
  Observable,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { LookupItem } from '../../../../core/models/lookup.models';
import { CommonService } from '../../../../shared/services/common.service';
import {
  ApiResponses,
  EditCaseDrawerParams,
  CaseDetailModel,
  CreatePreBipModel,
} from '../../insights.interface';
import { InsightsService } from '../../insights.service';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { CommonModule } from '@angular/common';
import { BehaviourSubListModel } from '../../../../core/models/common.models';
import { StudentSearchModel } from '../../../manage-organization/insights/insights.interface';
import { DrawerService } from '../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { differenceInCalendarDays, format } from 'date-fns';
import { OrganizationService } from '../../../organizations/organization.service';
import { LoaderService } from '../../../../core/services/loader.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { InsightMode } from '../../insights.enums';
import { ScheduleTemplate } from '../../../manage-settings/manage-settings.interface';
import { CustomTimeService } from '../../../manage-settings/custom-time/custom-time.service';
import {
  OrganizationDetailModel,
  userDetailModel,
} from '../../../organizations/organization.interface';
import { AuthService } from '../../../../core/services/auth.service';
import { ManageOrganizationService } from '../../../manage-organization/manage-organization.service';
import { LabelKey } from '../../../../core/models/report-labels.model';
import { ReportLabelsService } from '../../../manage-settings/report-labels/report-labels.service';
import { ReportLabelPipe } from '../../../../core/pipes/report-label.pipe';
import { NzModalModule } from 'ng-zorro-antd/modal';

@Component({
  selector: 'app-create-pre-bip-case',
  standalone: true,
  imports: [
    CommonModule,
    NzFormModule,
    NzFormItemComponent,
    NzFormItemComponent,
    NzFormControlComponent,
    NzInputModule,
    NzIconModule,
    NzSelectModule,
    NzDatePickerModule,
    NzButtonModule,
    SvgIconComponent,
    NzRadioModule,
    ReactiveFormsModule,
    FormsModule,
    NzAutocompleteModule,
    NzIconModule,
    NzSpinModule,
    ReportLabelPipe,
    NzModalModule,
  ],
  templateUrl: './create-pre-bip-case.component.html',
  styleUrl: './create-pre-bip-case.component.scss',
})
export class CreatePreBipCaseComponent implements OnInit, OnDestroy {
  preBipForm: FormGroup;
  genderList: LookupItem[] = [];
  raceList: LookupItem[] = [];
  specialEducationList: LookupItem[] = [];
  languageLevelList: LookupItem[] = [];
  organizationList: LookupItem[] = [];
  generalEducationList: LookupItem[] = [];
  studentList: StudentSearchModel[] = [];
  behaviourList: LookupItem[] = [];
  customScheduleList: ScheduleTemplate[] = [];
  behaviourSubTypeList: BehaviourSubListModel[] = [];
  selectedStudent: StudentSearchModel | null = null;
  selectedOrg: number | null = null;
  caseParams: EditCaseDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.CREATE,
  };
  caseData: CreatePreBipModel | null = null;

  userData: userDetailModel | null = null;
  searchInput = new Subject<string>();
  loading$: Observable<boolean>;
  loading = false;
  today = new Date();
  orgData: OrganizationDetailModel | null = null;
  orgId: number | null = null;
  disabledDate = (current: Date): boolean =>
    differenceInCalendarDays(current, this.today) > 0;

  isStudentSearchLoading = false;
  isOrganizationSearchLoading = false;
  isBehaviourSubTypeLoading = false;
  isScheduleLoading = false;
  needSafetyPlanLabel: string = LabelKey.NEEDS_SAFETY_PLAN;
  isTrainingWarningVisible = false;
  previousTrainingValue = false;
  IsIncludeDefaultSchedule = true;

  private readonly destroy$ = new Subject<void>();
  constructor(
    private insightsService: InsightsService,
    private commonService: CommonService,
    private cd: ChangeDetectorRef,
    private drawerService: DrawerService,
    private orgService: OrganizationService,
    private loaderService: LoaderService,
    private scheduleService: CustomTimeService,
    private authService: AuthService,
    private manageOrgService: ManageOrganizationService,
    private reportLabelsService: ReportLabelsService
  ) {
    this.reportLabelsService
      .getLabelValue(LabelKey.NEEDS_SAFETY_PLAN)
      .subscribe(value => {
        this.needSafetyPlanLabel = value;
      });
    this.authService.orgData$.subscribe(res => {
      this.orgData = res;
      this.orgId = this.orgData?.organizationId || 0;
    });
    this.authService.userData$.subscribe(res => {
      this.userData = res;
    });
    this.preBipForm = this.insightsService.bipForm(
      this.caseData ? this.caseData : null,
      ['bipTemplateId']
    );
    this.setupDrawerSubscription();
    this.loading$ = this.loaderService.loading$;
  }

  async ngOnInit() {
    this.preBipForm = this.insightsService.bipForm(
      this.caseData ? this.caseData : null,
      ['bipTemplateId']
    );
    await this.getMasterData();

    this.preBipForm.controls['studentSearch'].valueChanges
      .pipe(debounceTime(1000))
      .subscribe((val: string) => {
        if (!val) {
          if (this.selectedStudent) {
            this.clearSelectedStudent();
          }
        } else if (
          this.selectedStudent &&
          val ===
            `${this.selectedStudent.firstName} ${this.selectedStudent.lastName}`
        ) {
          this.onBlurStudent(val);
        }
      });

    this.preBipForm.controls['caseBehaviorId'].valueChanges.subscribe(
      (val: number) => {
        this.preBipForm.controls['caseBehaviorsType'].setValue(null);
        this.preBipForm.controls['caseBehaviorsType'].disable();
        this.behaviourSubTypeList = [];
        if (val) {
          this.getBehaviourSubTypes(val);
        }
      }
    );
    this.preBipForm.controls['organizationId'].valueChanges.subscribe(
      (val: string) => {
        if (!val) {
          this.preBipForm.controls['customScheduleId'].setValue(null);
          this.preBipForm.controls['customScheduleId'].disable();
        } else {
          this.preBipForm.controls['customScheduleId'].enable();
        }
        if (val && Number(val) !== this.selectedOrg) {
          this.selectedOrg = Number(val);
          this.customScheduleList = [];
          this.getCustomSchedules(Number(val));
        }
      }
    );
    this.searchInput
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe((val: string) => {
        this.isStudentSearchLoading = true;
        this.insightsService
          .searchStudentDetail(
            val,
            '',
            this.preBipForm.get('organizationId')?.value
          )
          .pipe(
            finalize(() => {
              this.isStudentSearchLoading = false;
            })
          )
          .subscribe({
            next: (res: ApiResponses<StudentSearchModel>) => {
              this.studentList = res.result || [];
              this.cd.markForCheck();
            },
            error: (error: Error) => {
              this.studentList = [];
              this.commonService.notify('error', error.message);
            },
          });
      });
  }

  private clearSelectedStudent(): void {
    this.selectedStudent = null;
    const controls = this.preBipForm.controls;
    [
      'studentId',
      'firstName',
      'lastName',
      'genderId',
      'dateOfBirth',
      'raceId',
    ].forEach(ctrl => {
      const control = controls[ctrl];
      if (control) {
        control.enable();
        control.setValue(null, { emitEvent: false });
      }
    });
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<EditCaseDrawerParams>(DRAWER_IDS.PREBIP)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          if (this.caseParams) {
            this.caseData = {
              dateOfBirth: this.caseParams?.case?.student.dateOfBirth || '',
              studentId: this.caseParams?.case?.student.enrollId || '',
              firstName: this.caseParams?.case?.student.firstName || '',
              genderId: Number(this.caseParams?.case?.student.genderId),
              lastName: this.caseParams?.case?.student.lastName || '',
              customScheduleId: Number(
                this.caseParams?.case?.student?.customScheduleId
              ),
              caseOwnerId: 1,
              organizationId: Number(
                this.caseParams?.case?.student.organizationId
              ),
              raceId: Number(this.caseParams?.case?.student.raceId),
              generalEducations:
                this.caseParams?.case?.generalEducations.map(g => {
                  return g.generalEducationId;
                }) || [],
              isEnabledSafetyPlan:
                this.caseParams?.case?.isEnabledSafetyPlan || false,
              isTrainingStudent:
                this.caseParams?.case?.isTrainingStudent || false,
              languageLevelId: Number(this.caseParams?.case?.languageLevelId),
              note: this.caseParams?.case?.note || '',
              specialEducations:
                this.caseParams?.case?.specialEducations.map(g => {
                  return g.specialEducationId;
                }) || [],
              caseBehaviorId: Number(
                this.caseParams?.case?.caseBehaviors[0].behaviorCategoryId
              ),
              caseBehaviorsType:
                this.caseParams?.case?.caseBehaviors[0].behaviorCategoryTypes.map(
                  c => {
                    return c.behaviorTypeId;
                  }
                ) || [],
            };
            this.selectedOrg = this.caseData.organizationId;
            this.getBehaviourSubTypes(Number(this.caseData?.caseBehaviorId));
            this.getCustomSchedules(Number(this.caseData?.organizationId));
          }
        }
      });
  }

  async getMasterData() {
    this.loaderService.show();
    const orgPromise: Promise<ApiResponses<LookupItem> | undefined> = this
      .userData?.isSuperAdmin
      ? this.commonService.getDataByType('Organization', '').toPromise()
      : (this.getAllOrganization(),
        Promise.resolve(undefined as ApiResponses<LookupItem> | undefined));

    await Promise.all([
      this.commonService.getDataByType('gender').toPromise(),
      this.commonService.getDataByType('races').toPromise(),
      this.commonService.getDataByType('SpecialEducation').toPromise(),
      this.commonService.getDataByType('generalEducation').toPromise(),
      this.commonService.getDataByType('LanguageLevels').toPromise(),
      orgPromise,
      this.commonService.getDataByType('BehaviorCategory').toPromise(),
    ])
      .then(
        ([
          genderList,
          raceList,
          specialEducationList,
          generalEducationList,
          languageLevelList,
          organizationList,
          behaviourList,
        ]: [
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
        ]) => {
          this.genderList = genderList ? genderList.result : [];
          this.raceList = raceList ? raceList.result : [];
          this.specialEducationList = specialEducationList
            ? specialEducationList.result
            : [];
          this.generalEducationList = generalEducationList
            ? generalEducationList.result
            : [];
          this.languageLevelList = languageLevelList
            ? languageLevelList.result
            : [];
          this.organizationList = organizationList
            ? organizationList.result
            : [];
          const organizationId =
            this.preBipForm.controls['organizationId'].value ||
            this.caseData?.organizationId ||
            this.orgId;
          if (organizationId) {
            this.preBipForm.controls['organizationId'].setValue(
              organizationId,
              {
                emitEvent: false,
              }
            );
          }
          this.preBipForm.controls['organizationId'].enable();
          this.getCustomSchedules(Number(this.orgId));
          this.behaviourList = behaviourList ? behaviourList.result : [];
          if (this.caseData) {
            const isOrgAvailable = this.organizationList.some(
              org => org.id === this.caseData?.organizationId
            );
            if (!isOrgAvailable) {
              this.getMissingOrganization(this.caseData.organizationId);
            }
          }
        }
      )
      .catch(e => console.error(e))
      .finally(() => this.loaderService.hide());
  }

  getBehaviourSubTypes(behaviourId: number) {
    this.isBehaviourSubTypeLoading = true;
    this.commonService
      .getBehaviourSubTypes(behaviourId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.preBipForm.controls['caseBehaviorsType'].enable();
          this.isBehaviourSubTypeLoading = false;
        })
      )
      .subscribe({
        next: (res: ApiResponses<BehaviourSubListModel>) => {
          this.behaviourSubTypeList = res.result;
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
        },
      });
  }

  getCustomSchedules(orgId: number) {
    this.isScheduleLoading = true;
    this.scheduleService
      .getAssignedScheduleListByOrgId(orgId, this.IsIncludeDefaultSchedule)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.preBipForm.controls['customScheduleId'].enable();
          this.isScheduleLoading = false;
        })
      )
      .subscribe({
        next: (res: ScheduleTemplate[]) => {
          this.customScheduleList = res;
          if (!this.preBipForm.controls['customScheduleId'].value) {
            const defaultSchedule = this.customScheduleList.find(
              schedule => schedule.isDefault
            );
            if (defaultSchedule) {
              this.preBipForm.controls['customScheduleId'].setValue(
                defaultSchedule.customScheduleId
              );
            }
          }
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
        },
      });
  }

  getMissingOrganization(orgId: number) {
    this.orgService.getOrganization(orgId).subscribe(res => {
      if (!!res && !!res.id) {
        this.organizationList.push({
          id: res.id,
          value: res.organizationName,
        });
      }
    });
  }

  onSearchStudent(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    if (value) {
      this.isStudentSearchLoading = true;
    }

    const currentValues: Record<string, unknown> = {
      specialEducations: this.preBipForm.get('specialEducations')?.value,
      generalEducations: this.preBipForm.get('generalEducations')?.value,
      languageLevelId: this.preBipForm.get('languageLevelId')?.value,
      note: this.preBipForm.get('note')?.value,
      isTrainingStudent: this.preBipForm.get('isTrainingStudent')?.value,
      isEnabledSafetyPlan: this.preBipForm.get('isEnabledSafetyPlan')?.value,
      customScheduleId: this.preBipForm.get('customScheduleId')?.value,
      caseBehaviorId: this.preBipForm.get('caseBehaviorId')?.value,
      caseBehaviorsType: this.preBipForm.get('caseBehaviorsType')?.value,
      organizationId: this.preBipForm.get('organizationId')?.value,
    };

    if (!value) {
      const controls = this.preBipForm.controls;
      controls['studentId'].enable();
    }

    this.searchInput.next(value);

    setTimeout(() => {
      Object.entries(currentValues).forEach(([key, value]) => {
        if (value !== undefined && value !== null && this.preBipForm.get(key)) {
          this.preBipForm.get(key)?.setValue(value, { emitEvent: false });
        }
      });
    }, 10);
  }

  async onBlurStudent(value: string) {
    const currentValues: Record<string, unknown> = {
      specialEducations: this.preBipForm.get('specialEducations')?.value,
      generalEducations: this.preBipForm.get('generalEducations')?.value,
      languageLevelId: this.preBipForm.get('languageLevelId')?.value,
      note: this.preBipForm.get('note')?.value,
      isTrainingStudent: this.preBipForm.get('isTrainingStudent')?.value,
      isEnabledSafetyPlan: this.preBipForm.get('isEnabledSafetyPlan')?.value,
      customScheduleId: this.preBipForm.get('customScheduleId')?.value,
      caseBehaviorId: this.preBipForm.get('caseBehaviorId')?.value,
      caseBehaviorsType: this.preBipForm.get('caseBehaviorsType')?.value,
    };

    const selectedStudent = this.selectedStudent
      ? `${this.selectedStudent.firstName} ${this.selectedStudent.lastName}`
      : null;
    const controls = this.preBipForm.controls;

    if (
      !!selectedStudent &&
      !!this.selectedStudent &&
      selectedStudent === value
    ) {
      const isOrgAvailable = this.organizationList.some(
        org => org.id === this.selectedStudent?.organizationId
      );
      if (!isOrgAvailable) {
        await this.getMissingOrganization(this.selectedStudent.organizationId);
      }

      controls['studentId'].setValue(this.selectedStudent.enrollId);
      controls['studentId'].disable();

      ['firstName', 'lastName', 'genderId', 'dateOfBirth', 'raceId'].forEach(
        ctrl => {
          controls[ctrl].disable();
          controls[ctrl].setValue(
            this.selectedStudent
              ? this.selectedStudent[ctrl as keyof StudentSearchModel]
              : ''
          );
        }
      );
    }

    setTimeout(() => {
      Object.entries(currentValues).forEach(([key, value]) => {
        if (value !== undefined && value !== null && this.preBipForm.get(key)) {
          this.preBipForm.get(key)?.setValue(value, { emitEvent: false });
        }
      });
    }, 100);
  }

  checkStudent() {
    const currentValues: Record<string, unknown> = {
      specialEducations: this.preBipForm.get('specialEducations')?.value,
      generalEducations: this.preBipForm.get('generalEducations')?.value,
      languageLevelId: this.preBipForm.get('languageLevelId')?.value,
      note: this.preBipForm.get('note')?.value,
      isTrainingStudent: this.preBipForm.get('isTrainingStudent')?.value,
      isEnabledSafetyPlan: this.preBipForm.get('isEnabledSafetyPlan')?.value,
      customScheduleId: this.preBipForm.get('customScheduleId')?.value,
      caseBehaviorId: this.preBipForm.get('caseBehaviorId')?.value,
      caseBehaviorsType: this.preBipForm.get('caseBehaviorsType')?.value,
    };

    const student = this.selectedStudent
      ? `${this.selectedStudent.firstName} ${this.selectedStudent.lastName}`
      : null;
    if (
      !!student &&
      student !== this.preBipForm.controls['studentSearch'].value
    ) {
      this.preBipForm.controls['studentSearch'].setValue(null, {
        emitEvent: false,
      });
    }

    setTimeout(() => {
      Object.entries(currentValues).forEach(([key, value]) => {
        if (value !== undefined && value !== null && this.preBipForm.get(key)) {
          this.preBipForm.get(key)?.setValue(value, { emitEvent: false });
        }
      });
    }, 100);
  }

  onSelect(student: StudentSearchModel): void {
    const currentValues: Record<string, unknown> = {
      specialEducations: this.preBipForm.get('specialEducations')?.value,
      generalEducations: this.preBipForm.get('generalEducations')?.value,
      languageLevelId: this.preBipForm.get('languageLevelId')?.value,
      note: this.preBipForm.get('note')?.value,
      isTrainingStudent: this.preBipForm.get('isTrainingStudent')?.value,
      isEnabledSafetyPlan: this.preBipForm.get('isEnabledSafetyPlan')?.value,
      customScheduleId: this.preBipForm.get('customScheduleId')?.value,
      caseBehaviorId: this.preBipForm.get('caseBehaviorId')?.value,
      caseBehaviorsType: this.preBipForm.get('caseBehaviorsType')?.value,
    };

    this.selectedStudent = student;

    setTimeout(() => {
      Object.entries(currentValues).forEach(([key, value]) => {
        if (value !== undefined && value !== null && this.preBipForm.get(key)) {
          this.preBipForm.get(key)?.setValue(value, { emitEvent: false });
        }
      });
    }, 100);
  }
  onSearchOrganization(value: string): void {
    this.isOrganizationSearchLoading = true;
    if (this.userData?.isSuperAdmin) {
      this.commonService
        .getDataByType('Organization', value)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            this.isOrganizationSearchLoading = false;
          })
        )
        .subscribe(orgList => {
          this.organizationList = orgList.result;
        });
    } else {
      this.getAllOrganization();
      this.isOrganizationSearchLoading = false;

      //TODO: Will remove after testing
      // this.manageOrgService
      //   .getAllowedOrganizationsToMoveUser(this.orgId ?? 0, value ?? '', false)
      //   .pipe(
      //     takeUntil(this.destroy$),
      //     finalize(() => {
      //       this.isOrganizationSearchLoading = false;
      //
      //     })
      //   )
      //   .subscribe(orgList => {
      //     this.organizationList = orgList.result;
      //   });
    }
  }

  getAllOrganization() {
    this.commonService.orgList$
      .pipe(takeUntil(this.destroy$))
      .subscribe(orgs => {
        this.organizationList = orgs;
      });
  }

  onSubmit() {
    if (this.preBipForm.invalid) {
      return;
    }
    this.loading = true;
    const form = this.preBipForm.getRawValue();
    const data = {
      ...form,
      dateOfBirth: format(
        this.preBipForm.controls['dateOfBirth'].value,
        'yyyy-MM-dd'
      ),
    };
    const operation$ =
      !!this.caseParams.case && this.caseParams.mode === InsightMode.EDIT
        ? this.insightsService.editPreBipCase(
            data,
            Number(this?.caseParams?.case?.id)
          )
        : this.insightsService.createPreBipCase(data);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: () => {
          this.handleSubmissionSuccess();
        },
        error: (error: Error) => {
          this.handleError(error);
        },
      });
  }

  private handleSubmissionSuccess(): void {
    this.showNotification();
    this.preBipForm.reset();
    this.closeDrawer();
  }

  private showNotification(): void {
    this.commonService.notify(
      'success',
      this.caseParams.mode === InsightMode.EDIT
        ? 'Pre Bip case has been updated successfully'
        : 'Pre Bip case has been created successfully',
      '',
      '450px'
    );
  }

  private handleError(error: Error): void {
    this.loading = false;

    this.commonService.notify('error', error.message);
  }

  closeDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.PREBIP_DETAIL);
    this.drawerService.closeDrawer(DRAWER_IDS.PREBIP);
  }

  onTrainingStudentChange(value: boolean): void {
    if (value === true) {
      this.previousTrainingValue =
        this.preBipForm.get('isTrainingStudent')?.value;
      this.isTrainingWarningVisible = true;
    }
  }

  handleCancelTrainingWarning(): void {
    this.isTrainingWarningVisible = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
